import React, { useState, useCallback, useMemo } from "react"

const PollWidget = ({ pollId, question, options: initialOptions = [], userId, onVote }) => {
  const [options, setOptions] = useState(() =>
    initialOptions.map(opt => ({
      id: opt.id || opt.label,
      label: opt.label || opt,
      votes: opt.votes || 0,
      voters: opt.voters || [],
    }))
  )
  const [userVote, setUserVote] = useState(() => {
    const voted = initialOptions.find(o => (o.voters || []).includes(userId))
    return voted ? (voted.id || voted.label) : null
  })
  const [submitting, setSubmitting] = useState(false)

  const totalVotes = useMemo(() => options.reduce((sum, o) => sum + o.votes, 0), [options])

  const handleVote = useCallback(async (optionId) => {
    if (userVote || submitting) return
    setSubmitting(true)

    // Optimistic update
    setUserVote(optionId)
    setOptions(prev => prev.map(o =>
      o.id === optionId ? { ...o, votes: o.votes + 1, voters: [...o.voters, userId] } : o
    ))

    try {
      if (onVote) {
        await onVote({ pollId, optionId, userId })
      }
    } catch (err) {
      console.error("PollWidget: Failed to vote:", err)
      // Revert
      setUserVote(null)
      setOptions(prev => prev.map(o =>
        o.id === optionId ? { ...o, votes: o.votes - 1, voters: o.voters.filter(v => v !== userId) } : o
      ))
    } finally {
      setSubmitting(false)
    }
  }, [pollId, userId, userVote, submitting, onVote])

  return (
    <div className="cf-poll-widget">
      <h4 className="cf-poll-widget__question">{question}</h4>
      <div className="cf-poll-widget__options">
        {options.map(opt => {
          const pct = totalVotes > 0 ? Math.round((opt.votes / totalVotes) * 100) : 0
          const isSelected = userVote === opt.id

          return (
            <button
              key={opt.id}
              className={`cf-poll-option ${isSelected ? "cf-poll-option--selected" : ""} ${userVote ? "cf-poll-option--voted" : ""}`}
              onClick={() => handleVote(opt.id)}
              disabled={!!userVote || submitting}
            >
              <div className="cf-poll-option__bar" style={{ width: userVote ? `${pct}%` : "0%" }} />
              <span className="cf-poll-option__label">{opt.label}</span>
              {userVote && <span className="cf-poll-option__pct">{pct}%</span>}
            </button>
          )
        })}
      </div>
      <div className="cf-poll-widget__footer">
        {totalVotes} vote{totalVotes !== 1 ? "s" : ""}
      </div>
    </div>
  )
}

export default PollWidget
